import React, { useState } from "react";
import { Form, Input, Button, Typography, Modal, message } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import styled from "styled-components";
import { motion } from "framer-motion";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { auth, db, requestForToken } from "../firebaseConfig";
import lemonLogo from "../assets/logo.png";

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * 1) Container теперь занимает всю ширину и высоту экрана (100vw, 100vh),
 *    все элементы центрируются и формируют column-layout.
 * 2) Добавлены паддинги по бокам (24px), чтобы на узком экране не прилипало
 *    к краям.
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;      /* занять всю ширину экрана */
  height: 100vh;     /* занять всю высоту экрана */
  padding: 0 24px;   /* отступы по бокам */
  background: #ffffff;
`;

/**
 * Анимированное лого по-прежнему центрируется (align-items: center в Container).
 * Размер сохраняется, можно при желании увеличить/уменьшить.
 */
const AnimatedLogo = styled(motion.img)`
  width: 88px;
  height: 88px;
  margin-top: 56px;
  margin-bottom: 24px;
`;

/**
 * 1) Задаём width: 100% – теперь форма растягивается на всю ширину контейнера.
 * 2) Убираем жёсткое max-width, чтобы было нормально на мобильных.
 * 3) Если хотите всё-таки ограничить максимальную ширину (например, на больших
 *    экранах), можно оставить max-width: 340px; но он не обязателен.
 */
const StyledForm = styled(Form)`
  width: 100%;          /* растягиваем на всю доступную ширину */
  /* max-width: 340px; */ /* можно раскомментировать, если нужна граница на десктопе */

  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input,
  .ant-input-password {
    width: 100%;        /* инпуты тоже во всю ширину */
    background: #f6f6f6;
    border: none;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 15px;
    box-sizing: border-box;
  }
`;

/**
 * Кнопка «Логин» теперь 100% ширины контейнера Form.Item.
 * Сохраняем все остальные стили (радиусы, цвета).
 */
const PrimaryButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 24px !important;
  border: none;
  background: #ffd233;
  color: #000;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: transform 0.2s ease;

  &:hover,
  &:focus {
    background: #ffcc00;
    color: #000;
    border: none;
  }

  &:active {
    transform: scale(0.96);
  }
`;

/**
 * Ссылочные кнопки (link) делаем блочными и по ширине 100%, чтобы они 
 * центрировались в колонке. Если нужно, чтобы они не растягивались по ширине, 
 * можно убрать width: 100%, и они займут только размер текста.
 */
const LinkButton = styled(Button)`
  display: block;
  width: 100%;
  padding: 0;
  height: auto;
  border: none;
  background: transparent;
  color: #ffd233 !important;
  text-align: center;

  &:hover,
  &:focus {
    color: #ffcc00 !important;
    text-decoration: none;
  }
`;

/**
 * Нижний блок «Нет аккаунта? Зарегистрироваться!» теперь тоже по центру,
 * занимая всю ширину родителя.
 */
const BottomBlock = styled.div`
  margin-top: auto;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;  /* переключили на колонку, чтобы кнопка всегда была снизу */
  align-items: center;
  width: 100%;
  gap: 8px;
`;

/**
 * Стили для message
 */
const MessageStyles = styled.div`
  .ant-message {
    top: 24px;
  }
  
  .ant-message-notice-content {
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .ant-message-success {
    background: #f6ffed;
    border: 1px solid #b7eb8f;
  }
  
  .ant-message-error {
    background: #fff2f0;
    border: 1px solid #ffccc7;
  }
  
  .ant-message-warning {
    background: #fffbe6;
    border: 1px solid #ffe58f;
  }
`;

/* -------------------------------------------------------------------------- */
/*                                   Component                                */
/* -------------------------------------------------------------------------- */

const { Title, Text } = Typography;

const Login = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Konfiguracja message
  React.useEffect(() => {
    message.config({
      top: 24,
      duration: 3,
      maxCount: 3,
      rtl: false,
    });
  }, []);

  // Функция для получения польского сообщения об ошибке на основе кода ошибки Firebase
  const getPolishErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/invalid-email': 'Nieprawidłowy format adresu e-mail.',
      'auth/user-disabled': 'To konto zostało wyłączone. Skontaktuj się z administracją.',
      'auth/user-not-found': 'Nie znaleziono użytkownika z tym adresem e-mail.',
      'auth/wrong-password': 'Nieprawidłowe hasło.',
      'auth/invalid-credential': 'Nieprawidłowe dane logowania. Sprawdź e-mail i hasło.',
      'auth/too-many-requests': 'Zbyt wiele nieudanych prób logowania. Spróbuj ponownie później.',
      'auth/network-request-failed': 'Błąd połączenia z siecią. Sprawdź swoje połączenie internetowe.',
      'auth/operation-not-allowed': 'Logowanie za pomocą e-mail i hasła jest wyłączone.',
      'auth/email-already-in-use': 'Ten adres e-mail jest już używany.',
      'auth/weak-password': 'Hasło jest zbyt słabe.',
    };
    
    return errorMessages[errorCode] || 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';
  };

  const onFinish = async ({ email, password }) => {
    setLoading(true);
    const key = 'login';
    
    // Pokazujemy loading message
    messageApi.open({
      key,
      type: 'loading',
      content: 'Logowanie...',
      duration: 0,
    });
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      // Próba uzyskania i zapisania tokenu FCM
      try {
        const token = await requestForToken();
        if (token) {
          const userDocRef = doc(db, "users", user.uid);
          await setDoc(userDocRef, { fcmToken: token }, { merge: true });
        }
      } catch (tokenError) {
        console.error("Nie udało się uzyskać tokenu FCM:", tokenError);
      }

      // Pokazujemy sukces
      messageApi.open({
        key,
        type: 'success',
        content: 'Zalogowano pomyślnie! Przekierowywanie...',
        duration: 2,
      });
      
      // Krótkie opóźnienie przed przekierowaniem dla lepszego UX
      setTimeout(() => {
        navigate("/");
      }, 1000);
      
    } catch (err) {
      console.error("Błąd logowania:", err);
      console.error("Kod błędu:", err.code);
      console.error("Wiadomość błędu:", err.message);
      
      // Pobieramy odpowiednie polskie komunikaty błędów
      const errorMessage = getPolishErrorMessage(err.code);
      console.log("Pokazuję komunikat błędu:", errorMessage);
      
      // Pokazujemy błąd
      messageApi.open({
        key,
        type: 'error',
        content: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const openResetModal = () => {
    setResetEmail("");
    setIsModalVisible(true);
  };
  
  const closeResetModal = () => setIsModalVisible(false);

  const handleReset = async () => {
    if (!resetEmail) {
      messageApi.warning('Wprowadź adres e-mail.');
      return;
    }

    setLoading(true);
    const key = 'reset';
    
    // Pokazujemy loading message
    messageApi.open({
      key,
      type: 'loading',
      content: 'Wysyłanie linku resetującego...',
      duration: 0,
    });
    
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      
      // Pokazujemy sukces
      messageApi.open({
        key,
        type: 'success',
        content: `Link do resetowania hasła został wysłany na adres ${resetEmail}.`,
        duration: 5,
      });
      
      closeResetModal();
    } catch (err) {
      console.error("Błąd resetowania hasła:", err);
      console.error("Kod błędu:", err.code);
      
      let errorMessage = "Nie udało się wysłać linku resetującego.";
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = "Nie znaleziono użytkownika z tym adresem e-mail.";
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = "Nieprawidłowy format adresu e-mail.";
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = "Zbyt wiele prób. Spróbuj ponownie później.";
      }
      
      // Pokazujemy błąd
      messageApi.open({
        key,
        type: 'error',
        content: errorMessage,
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MessageStyles>
      {contextHolder}
      <Container>
        {/* Логотип: центрирован через align-items: center у Container */}
        <AnimatedLogo
        src={lemonLogo}
        alt="Logo Spario"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={loading ? { rotate: 360 } : { scale: 1, opacity: 1 }}
        transition={
          loading
            ? { duration: 1, ease: "linear", repeat: Infinity }
            : { duration: 0.6, ease: "easeOut" }
        }
      />

      <Title level={4} style={{ marginBottom: 4, textAlign: "center", width: "100%" }}>
        Witaj w Spario!
      </Title>
      <Text type="secondary" style={{ marginBottom: 32, textAlign: "center", width: "100%" }}>
        Innowacyjne rozliczenia dla nowoczesnych taksówkarzy
      </Text>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        style={{ width: "100%" }}
      >
        <StyledForm
          form={form}
          name="login"
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
        >
          <Form.Item
            label="E-mail"
            name="email"
            rules={[
              { required: true, message: "Wprowadź adres e-mail" },
              { type: "email", message: "Nieprawidłowy adres e-mail" },
            ]}
          >
            <Input placeholder="janedoe@gmail.com" size="large" disabled={loading} />
          </Form.Item>

          <Form.Item
            label="Hasło"
            name="password"
            rules={[{ required: true, message: "Wprowadź hasło" }]}
          >
            <Input.Password
              placeholder="••••••••"
              size="large"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item>
            <PrimaryButton type="primary" htmlType="submit" disabled={loading}>
              {loading ? "Logowanie..." : "ZALOGUJ SIĘ"}
            </PrimaryButton>
          </Form.Item>
        </StyledForm>
      </motion.div>

      {/* Ссылку «Забыли пароль?» тоже сделаем по центру и на всю ширину */}
      <LinkButton type="link" onClick={openResetModal} disabled={loading}>
        Zapomniałeś hasła?
      </LinkButton>

      <BottomBlock>
        <Text>Nie masz konta?</Text>
        <LinkButton type="link" onClick={() => navigate("/register")}>
          Zarejestruj się!
        </LinkButton>
      </BottomBlock>

      {/* ------------------------------ reset modal ------------------------------ */}
      <Modal
        title="Resetuj hasło"
        open={isModalVisible}
        onOk={handleReset}
        onCancel={closeResetModal}
        okText="Wyślij link resetujący"
        cancelText="Anuluj"
        confirmLoading={loading}
        maskClosable={!loading}
      >
        <Form layout="vertical">
          <Form.Item
            label="Adres e-mail"
            rules={[
              { required: true, message: "Wprowadź adres e-mail" },
              { type: "email", message: "Nieprawidłowy adres e-mail" },
            ]}
          >
            <Input
              placeholder="you@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={loading}
            />
          </Form.Item>
        </Form>
      </Modal>
      </Container>
    </MessageStyles>
  );
};

export default Login;